import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { updateInventoryItem, deleteInventoryItem } from '../services/firestoreService';
import { generateInventoryItemPDF } from '../services/pdfService';
import RecordAuditBlock from '../components/common/RecordAuditBlock';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import Modal from '../components/common/Modal';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import { formatCurrency, formatDate, parseDateInputValue } from '../utils/helpers';
import toast from 'react-hot-toast';

const formatItemDate = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.slice(0, 10);
  if (value?.seconds) return new Date(value.seconds * 1000).toISOString().slice(0, 10);
  if (value?.toDate) return value.toDate().toISOString().slice(0, 10);
  return '';
};

const InventoryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const auditActor = user ? { uid: user.uid, email: user.email } : null;
  const { inventory, loadingInventory } = useApp();

  const item = useMemo(() => inventory.find((i) => i.id === id), [inventory, id]);

  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', total: '', quantity: '1', date: '' });

  const itemTotal = item?.total ?? item?.price ?? 0;

  const openEdit = () => {
    if (!item) return;
    setFormData({
      name: item.name || '',
      description: item.description || '',
      total: String(itemTotal),
      quantity: String(item.quantity || '1'),
      date: formatItemDate(item.date) || new Date().toISOString().slice(0, 10)
    });
    setShowEditModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!item) return;
    setSubmitting(true);
    const result = await updateInventoryItem(item.id, {
      name: formData.name.trim(),
      description: formData.description.trim(),
      total: Number(formData.total),
      quantity: Number(formData.quantity),
      date: parseDateInputValue(formData.date),
      auditActor
    });
    if (result.success) {
      toast.success('Waa la cusbooneysiiyay');
      setShowEditModal(false);
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handleDelete = async () => {
    if (!item) return;
    setSubmitting(true);
    const result = await deleteInventoryItem(item.id);
    if (result.success) {
      toast.success('Waa la tirtiray');
      navigate('/admin/inventory');
    } else {
      toast.error(result.error);
    }
    setSubmitting(false);
  };

  const handlePdf = async () => {
    if (!item) return;
    setDownloadingPdf(true);
    try {
      await generateInventoryItemPDF(item);
      toast.success('PDF waa la soo dejiyay');
    } catch {
      toast.error('PDF khalad');
    }
    setDownloadingPdf(false);
  };

  if (loadingInventory) return <Loading fullScreen />;

  if (!item) {
    return (
      <div className="max-w-lg mx-auto text-center py-16 space-y-4">
        <p className="text-gray-600">Alaabta lama helin.</p>
        <Button onClick={() => navigate('/admin/inventory')}>← Liiska inventory</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="flex flex-wrap gap-2 justify-between items-center">
        <Button variant="ghost" onClick={() => navigate('/admin/inventory')}>← Liiska inventory</Button>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePdf} loading={downloadingPdf}>Soo deji PDF</Button>
          <Button variant="outline" onClick={openEdit}>Wax ka beddel</Button>
          <Button variant="danger" onClick={() => setShowDeleteModal(true)}>Tirtir</Button>
        </div>
      </div>

      <Card title={item.name} icon={<span className="text-2xl">📦</span>}>
        <div className="space-y-3 text-sm">
          <p><span className="text-gray-500">Sharaxaad:</span>{' '}
            <span className="font-medium">{item.description || '—'}</span>
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center">
              <p className="text-gray-500 text-xs">Tirada</p>
              <p className="text-2xl font-bold text-blue-800">{item.quantity ?? 0}</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-gray-500 text-xs">Wadarta</p>
              <p className="text-2xl font-bold text-green-700">{formatCurrency(itemTotal)}</p>
            </div>
          </div>
          <p><span className="text-gray-500">Taariikh (record):</span> {formatDate(item.date)}</p>
        </div>
        <RecordAuditBlock record={item} />
      </Card>

      <Modal isOpen={showEditModal} onClose={() => setShowEditModal(false)} title="Wax ka bedel alaabta">
        <form onSubmit={handleSave} className="space-y-4">
          <Input label="Magaca" name="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
          <Input label="Sharaxaad" name="description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Tirada" name="quantity" type="number" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} required />
            <Input label="Wadarta ($)" name="total" type="number" step="0.01" value={formData.total} onChange={(e) => setFormData({ ...formData, total: e.target.value })} required />
          </div>
          <Input label="Taariikh" name="date" type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required />
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={() => setShowEditModal(false)} fullWidth>Ka noqo</Button>
            <Button type="submit" loading={submitting} fullWidth>Kaydi</Button>
          </div>
        </form>
      </Modal>

      <Modal isOpen={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Tirtir alaabta" size="sm">
        <p className="text-gray-600 text-sm mb-4">Ma hubtaa inaad tirtirto <strong>{item.name}</strong>?</p>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={() => setShowDeleteModal(false)} fullWidth>Maya</Button>
          <Button variant="danger" onClick={handleDelete} loading={submitting} fullWidth>Haa, tirtir</Button>
        </div>
      </Modal>
    </div>
  );
};

export default InventoryDetail;
